// ==================== AGENDAMENTOS DO CLIENTE - CÓDIGO DE CONFIRMAÇÃO ====================
import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  Calendar, Clock, CheckCircle2, XCircle, Loader2, AlertCircle, 
  RefreshCw, Plus, Car, MapPin, Timer, KeyRound, Copy, AlertTriangle,
  MapPinned
} from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import AppointmentCountdown from '@/components/AppointmentCountdown';

const REFRESH_INTERVAL = 15000;

// Pass 10C-VII: status ativos (topo) e mapa de filtros do histórico (não altera a máquina de estados)
const ACTIVE_STATUSES = ['PENDING', 'PENDING_PROVIDER_ACCEPTANCE', 'PENDING_PROVIDER_CONFIRMATION', 'ACCEPTED', 'IN_PROGRESS', 'AWAITING_CODE'];
const FILTER_STATUSES = {
  all: ['COMPLETED', 'PAID', 'REJECTED', 'CANCELLED', 'CANCELLED_TIMEOUT', 'EXPIRED'],
  completed: ['COMPLETED', 'PAID'],
  cancelled: ['REJECTED', 'CANCELLED', 'CANCELLED_TIMEOUT'],
  expired: ['EXPIRED'],
};
// Pass 10C-IX: rótulos de serviço e formatação de valor (dados reais do appointment)
const SERVICE_TYPE_LABELS = {
  complete: 'Lavagem Completa',
  lavagem_completa: 'Lavagem Completa',
  ducha: 'Ducha Simples',
  ducha_simples: 'Ducha Simples',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

export default function ClientAppointments() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [activeItems, setActiveItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyFilter, setHistoryFilter] = useState('all');
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const [view, setView] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Modal de código
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeData, setCodeData] = useState(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  
  // Modal de contestação
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const [disputeAppointment, setDisputeAppointment] = useState(null);
  const [disputeMotivo, setDisputeMotivo] = useState('');
  const [disputeDescricao, setDisputeDescricao] = useState('');
  const [disputeLoading, setDisputeLoading] = useState(false);
  const [disputedAppointments, setDisputedAppointments] = useState([]);
  
  // Check-in state
  const [checkinLoading, setCheckinLoading] = useState(null);

  const buildClientUrl = (statusList, cursor, pageLimit) => {
    const usp = new URLSearchParams();
    (statusList || []).forEach((s) => usp.append('statuses', s));
    usp.append('limit', String(pageLimit));
    if (cursor) usp.append('cursor', cursor);
    return `${API}/appointments/client?${usp.toString()}`;
  };

  // Ativos: busca todos os status ativos e ordena por proximidade do serviço (date+time ASC)
  const fetchActive = useCallback(async () => {
    const token = localStorage.getItem('token');
    const res = await axios.get(buildClientUrl(ACTIVE_STATUSES, null, 100), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const items = (res.data.appointments || []).slice().sort(
      (a, b) => `${a.date || ''}${a.time || ''}`.localeCompare(`${b.date || ''}${b.time || ''}`)
    );
    setActiveItems(items);
  }, []);

  // Histórico: página por cursor. replace=true reinicia a lista (troca de filtro/refresh)
  const fetchHistoryPage = useCallback(async (filterKey, cursor, replace) => {
    const token = localStorage.getItem('token');
    const res = await axios.get(buildClientUrl(FILTER_STATUSES[filterKey], cursor, 20), {
      headers: { Authorization: `Bearer ${token}` }
    });
    const newItems = res.data.appointments || [];
    setHistoryTotal(res.data.total || 0);
    setHistoryHasMore(!!res.data.has_more);
    setHistoryCursor(res.data.next_cursor || null);
    setHistoryItems((prev) => {
      const baseList = replace ? [] : prev;
      const seen = new Set(baseList.map((a) => a.id));
      const merged = baseList.slice();
      newItems.forEach((it) => { if (!seen.has(it.id)) { merged.push(it); seen.add(it.id); } });
      return merged;
    });
  }, []);

  // Carrega ativos + 1ª página do histórico do filtro atual (usado no mount, refresh e auto-refresh)
  const loadAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      await Promise.all([fetchActive(), fetchHistoryPage(historyFilter, null, true)]);
      setLastRefresh(new Date());
    } catch (error) {
      if (!silent) toast.error('Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  }, [fetchActive, fetchHistoryPage, historyFilter]);

  // Ver mais: próxima página do filtro atual, mantendo os já exibidos (sem duplicar)
  const loadMore = async () => {
    if (!historyHasMore || !historyCursor) return;
    setHistoryLoadingMore(true);
    try {
      await fetchHistoryPage(historyFilter, historyCursor, false);
    } finally {
      setHistoryLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!user || user.user_type !== 'client') {
      navigate('/client/login');
      return;
    }
    loadAll();
    fetchMyDisputes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate]);

  // Auto-refresh de 15s: reinicia a 1ª página do histórico do filtro atual (sem duplicar)
  useEffect(() => {
    if (!user || user.user_type !== 'client') return;
    const interval = setInterval(() => loadAll(true), REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [user, loadAll]);

  // Buscar disputas existentes do cliente
  const fetchMyDisputes = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/disputes/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const appointmentIds = (res.data.disputes || []).map(d => d.appointment_id);
      setDisputedAppointments(appointmentIds);
    } catch (error) {
      console.error('Erro ao buscar disputas:', error);
    }
  };

  // ==================== CHECK-IN DO CLIENTE ====================
  const handleCheckin = async (appointmentId) => {
    setCheckinLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/appointments/${appointmentId}/checkin`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.already_checked_in) {
        toast.info('Você já fez check-in neste agendamento');
      } else {
        toast.success('✅ Check-in realizado! O prestador foi notificado.');
      }
      
      // Atualizar lista de agendamentos
      loadAll(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao fazer check-in');
    } finally {
      setCheckinLoading(null);
    }
  };

  // ==================== SAFE PATCH: JANELA DE CHECK-IN ====================
  // ==================== SAFE PATCH: JANELA DE CHECK-IN ====================
  // Fonte única de verdade: o backend envia appointment.checkin (calculado por
  // compute_checkin_window). O frontend apenas lê esses valores — sem qualquer
  // cálculo de janela/tempo aqui.

  // Verificar se já fez check-in
  const hasCheckedIn = (appointment) => {
    return appointment.client_checked_in === true;
  };
  // ==================== FIM SAFE PATCH: JANELA DE CHECK-IN ====================

  // Abrir modal de contestação
  const handleOpenDispute = (appointment) => {
    setDisputeAppointment(appointment);
    setDisputeMotivo('');
    setDisputeDescricao('');
    setDisputeModalOpen(true);
  };

  // Enviar contestação
  const handleSubmitDispute = async () => {
    if (!disputeMotivo) {
      toast.error('Selecione o motivo da contestação');
      return;
    }
    if (!disputeDescricao || disputeDescricao.length < 20) {
      toast.error('Descreva o problema com pelo menos 20 caracteres');
      return;
    }

    setDisputeLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/disputes`, {
        appointment_id: disputeAppointment.id,
        motivo: disputeMotivo,
        descricao: disputeDescricao
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Contestação enviada para análise!');
      setDisputeModalOpen(false);
      setDisputedAppointments(prev => [...prev, disputeAppointment.id]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar contestação');
    } finally {
      setDisputeLoading(false);
    }
  };

  // Verificar se pode contestar (serviço concluído e não contestado)
  const canDispute = (appointment) => {
    const isCompleted = ['COMPLETED', 'PAID'].includes(appointment.status);
    const notDisputed = !disputedAppointments.includes(appointment.id);
    return isCompleted && notDisputed;
  };

  const handleViewCode = async (appointment) => {
    setSelectedAppointment(appointment);
    setCodeLoading(true);
    setCodeModalOpen(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/appointments/${appointment.id}/code`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setCodeData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao obter código');
      setCodeModalOpen(false);
    } finally {
      setCodeLoading(false);
    }
  };

  const copyCode = () => {
    if (codeData?.code) {
      navigator.clipboard.writeText(codeData.code);
      toast.success('Código copiado!');
    }
  };

  const handleCancelAppointment = async (appointmentId, isTimeout = false) => {
    const message = isTimeout 
      ? 'O prestador não respondeu no tempo. Deseja cancelar?'
      : 'Deseja realmente cancelar este agendamento?';
    
    if (!confirm(message)) return;

    setCancelLoading(appointmentId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/appointments/${appointmentId}/cancel`,
        { reason: isTimeout ? 'Timeout' : 'Cancelado pelo cliente' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Agendamento cancelado');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar');
    } finally {
      setCancelLoading(null);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      PENDING: { 
        text: 'Aguardando confirmação do prestador',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: Clock,
        showCancel: true,
        showCode: false,
        borderColor: 'border-l-yellow-400'
      },
      PENDING_PROVIDER_ACCEPTANCE: { 
        text: 'Aguardando confirmação do prestador',
        color: 'bg-amber-100 text-amber-800 border-amber-300',
        icon: Clock,
        showCancel: true,
        showCode: false,
        borderColor: 'border-l-amber-400'
      },
      PENDING_PROVIDER_CONFIRMATION: { 
        text: 'Aguardando confirmação do prestador',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
        icon: Clock,
        showCancel: true,
        showCode: false,
        borderColor: 'border-l-yellow-400'
      },
      ACCEPTED: { 
        text: 'Confirmado - Aguardando dia do serviço',
        color: 'bg-green-100 text-green-800 border-green-300',
        icon: CheckCircle2,
        showCancel: true,
        showCode: false,
        borderColor: 'border-l-green-400'
      },
      IN_PROGRESS: { 
        text: 'Serviço em andamento',
        color: 'bg-blue-100 text-blue-800 border-blue-300',
        icon: Loader2,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-blue-400'
      },
      AWAITING_CODE: { 
        text: 'Serviço finalizado - Informe o código ao prestador',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-400',
        icon: KeyRound,
        showCancel: false,
        showCode: true,
        borderColor: 'border-l-yellow-500'
      },
      COMPLETED: { 
        text: 'Serviço concluído',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
        icon: CheckCircle2,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-emerald-500'
      },
      PAID: { 
        text: 'Serviço concluído e pago',
        color: 'bg-emerald-100 text-emerald-800 border-emerald-300',
        icon: CheckCircle2,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-emerald-500'
      },
      REJECTED: { 
        text: 'Recusado pelo prestador',
        color: 'bg-red-100 text-red-800 border-red-300',
        icon: XCircle,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-red-400'
      },
      CANCELLED: { 
        text: 'Cancelado',
        color: 'bg-gray-100 text-gray-800 border-gray-300',
        icon: XCircle,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-gray-400'
      },
      CANCELLED_TIMEOUT: { 
        text: 'Cancelado (sem resposta)',
        color: 'bg-gray-100 text-gray-600 border-gray-300',
        icon: Timer,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-gray-400'
      },
      EXPIRED: { 
        text: 'Expirado',
        color: 'bg-gray-100 text-gray-600 border-gray-300',
        icon: AlertCircle,
        showCancel: false,
        showCode: false,
        borderColor: 'border-l-gray-400'
      }
    };
    
    return configs[status] || configs.PENDING;
  };

  const renderAppointmentCard = (appointment) => {
    const config = getStatusConfig(appointment.status);
    const Icon = config.icon;
    const isLoading = cancelLoading === appointment.id;
    
    // Calcular timeout
    let timeoutMinutes = undefined;
    if (['PENDING', 'PENDING_PROVIDER_ACCEPTANCE', 'PENDING_PROVIDER_CONFIRMATION'].includes(appointment.status) && appointment.expires_at) {
      try {
        const expires = new Date(appointment.expires_at);
        const now = new Date();
        timeoutMinutes = (expires - now) / 60000;
      } catch {}
    }
    const canCancelTimeout = timeoutMinutes !== undefined && timeoutMinutes <= 0;
    
    return (
      <Card 
        key={appointment.id} 
        className={`p-4 border-l-4 ${config.borderColor}`}
        data-testid={`appointment-${appointment.id}`}
      >
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{appointment.provider_name}</h3>
            
            <div className="flex flex-wrap gap-2 mt-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {appointment.time}
              </span>
              {appointment.provider_address && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {appointment.provider_address}
                </span>
              )}
            </div>
            
            {/* Contador regressivo para agendamentos ativos */}
            {/* Regra oficial: durante PENDING_PROVIDER_ACCEPTANCE o foco é a
                confirmação do prestador — o countdown do horário NÃO aparece.
                Ele volta a ser exibido a partir de ACCEPTED. */}
            {['PENDING', 'PENDING_PROVIDER_CONFIRMATION', 'ACCEPTED', 'IN_PROGRESS'].includes(appointment.status) && (
              <div className="mt-3">
                <AppointmentCountdown 
                  date={appointment.date} 
                  time={appointment.time}
                  size="md"
                  showLabel={true}
                />
              </div>
            )}

            {appointment.vehicle_plate && (
              <div className="flex items-center gap-2 mt-2">
                <Car className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm">{appointment.vehicle_brand} {appointment.vehicle_model}</span>
                <Badge variant="outline" className="font-mono text-xs">{appointment.vehicle_plate}</Badge>
              </div>
            )}
          </div>
          
          <div className="text-right">
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
              <Icon className={`w-3 h-3 ${appointment.status === 'IN_PROGRESS' ? 'animate-spin' : ''}`} />
              {config.text.split(' - ')[0]}
            </span>
          </div>
        </div>

        {/* Alerta para AWAITING_CODE */}
        {config.showCode && (
          <Alert className="mb-3 py-3 bg-yellow-50 border-yellow-300">
            <KeyRound className="h-5 w-5 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>Serviço finalizado!</strong> Informe o código abaixo ao prestador para confirmar.
            </AlertDescription>
          </Alert>
        )}

        {/* Botões de ação */}
        <div className="flex gap-2 flex-wrap">
          {/* ==================== SAFE PATCH: Check-in com janela de tempo ==================== */}
          {/* Mensagem contextual discreta (substitui o banner amarelo superior) */}
          {(appointment.status === 'ACCEPTED' && !appointment.client_checked_in && appointment.checkin?.can_checkin) && (
            <div
              className="w-full mb-1 text-sm text-slate-600"
              data-testid={`checkin-hint-${appointment.id}`}
            >
              <p className="font-medium text-slate-800">Sua lavagem começa em breve!</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Faça check-in para avisar que você está a caminho.
              </p>
            </div>
          )}

          {/* Mostrar se janela de check-in expirou */}
          {(appointment.status === 'ACCEPTED' && !appointment.client_checked_in && appointment.checkin?.checkin_expired) && (
            <div 
              className="flex-1 flex items-center justify-center gap-2 py-2 px-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
              data-testid={`checkin-expired-${appointment.id}`}
            >
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Prazo de check-in expirado</span>
            </div>
          )}
          
          {/* Botão de Check-in (só aparece dentro da janela) */}
          {(appointment.status === 'ACCEPTED' && !appointment.client_checked_in && appointment.checkin?.can_checkin) && (
            <Button
              onClick={() => handleCheckin(appointment.id)}
              disabled={checkinLoading === appointment.id}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid={`checkin-btn-${appointment.id}`}
            >
              {checkinLoading === appointment.id ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <MapPinned className="w-4 h-4 mr-2" />
              )}
              Fazer Check-in
            </Button>
          )}
          {/* ==================== FIM SAFE PATCH ==================== */}
          
          {/* Badge de check-in realizado */}
          {hasCheckedIn(appointment) && appointment.status === 'ACCEPTED' && (
            <Badge className="bg-green-100 text-green-800 border-green-300 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              Check-in realizado
            </Badge>
          )}
          
          {config.showCode && (
            <Button
              onClick={() => handleViewCode(appointment)}
              className="flex-1 bg-yellow-600 hover:bg-yellow-700 animate-pulse"
              data-testid={`code-btn-${appointment.id}`}
            >
              <KeyRound className="w-4 h-4 mr-2" />
              Ver Código de Confirmação
            </Button>
          )}
          
          {config.showCancel && (
            <Button
              variant={canCancelTimeout ? "destructive" : "outline"}
              size="sm"
              onClick={() => handleCancelAppointment(appointment.id, canCancelTimeout)}
              disabled={isLoading}
              className={config.showCode || (appointment.status === 'ACCEPTED' && !appointment.client_checked_in && appointment.checkin?.can_checkin) ? "" : "w-full"}
              data-testid={`cancel-btn-${appointment.id}`}
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancelar'}
            </Button>
          )}
          
          {/* Botão de Contestação para serviços concluídos */}
          {canDispute(appointment) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleOpenDispute(appointment)}
              className="text-orange-600 border-orange-300 hover:bg-orange-50"
              data-testid={`dispute-btn-${appointment.id}`}
            >
              <AlertTriangle className="w-4 h-4 mr-1" />
              Abrir Contestação
            </Button>
          )}
          
          {/* Badge se já tem contestação aberta */}
          {disputedAppointments.includes(appointment.id) && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              Contestação em análise
            </Badge>
          )}
        </div>
      </Card>
    );
  };

  // Pass 10C-IX: card compacto do histórico (dados reais: serviço + valor quando existir).
  // Preserva status individual e a ação de contestação (única aplicável a status históricos).
  const renderHistoryCard = (appointment) => {
    const config = getStatusConfig(appointment.status);
    const Icon = config.icon;
    const priceVal = appointment.total_price || appointment.price || 0;
    const serviceLabel = SERVICE_TYPE_LABELS[appointment.service_type] || appointment.service_type;
    return (
      <Card
        key={appointment.id}
        className={`p-4 border-l-4 ${config.borderColor}`}
        data-testid={`appointment-${appointment.id}`}
      >
        <div className="flex justify-between items-start gap-3">
          <div className="min-w-0">
            <h3 className="font-semibold text-base truncate">{appointment.provider_name}</h3>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(appointment.date + 'T00:00:00').toLocaleDateString('pt-BR')}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {appointment.time}
              </span>
            </div>
            {serviceLabel && (
              <p className="text-sm text-slate-600 mt-1" data-testid={`history-service-${appointment.id}`}>{serviceLabel}</p>
            )}
          </div>
          <div className="text-right shrink-0">
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}>
              <Icon className="w-3 h-3" />
              {config.text.split(' - ')[0]}
            </span>
            {priceVal > 0 && (
              <p className="mt-2 font-semibold text-slate-900" data-testid={`history-price-${appointment.id}`}>
                {formatCurrency(priceVal)}
              </p>
            )}
          </div>
        </div>
        {(canDispute(appointment) || disputedAppointments.includes(appointment.id)) && (
          <div className="flex gap-2 flex-wrap mt-3">
            {canDispute(appointment) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleOpenDispute(appointment)}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
                data-testid={`dispute-btn-${appointment.id}`}
              >
                <AlertTriangle className="w-4 h-4 mr-1" />
                Abrir Contestação
              </Button>
            )}
            {disputedAppointments.includes(appointment.id) && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                Contestação em análise
              </Badge>
            )}
          </div>
        )}
      </Card>
    );
  };

  // Pass 10C-VII: ativos e histórico agora vêm de fetches paginados separados
  // (activeItems / historyItems). Sem slice(0,10) e sem teto de 100 no histórico.

  if (loading && activeItems.length === 0 && historyItems.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header Fixo */}
      <div className="bg-white border-b sticky top-10 z-10">
        <div className="max-w-2xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/client/dashboard')}
              data-testid="back-to-dashboard-btn"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="m15 18-6-6 6-6"/></svg>
            </Button>
            <h1 className="text-xl font-bold">Agendamentos</h1>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => loadAll()}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
            <Button onClick={() => navigate('/client/schedule')} size="sm" data-testid="new-appointment-btn">
              <Plus className="w-4 h-4 mr-1" />
              Novo
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-4">
        {/* Modal de Código */}
        <Dialog open={codeModalOpen} onOpenChange={setCodeModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Código de Confirmação
              </DialogTitle>
              <DialogDescription>
                Informe este código ao prestador após a lavagem do veículo
              </DialogDescription>
            </DialogHeader>
            
            {codeLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                <p className="text-sm text-muted-foreground">Carregando código...</p>
              </div>
            ) : codeData ? (
              <div className="flex flex-col items-center py-4">
                {/* Código grande */}
                <div 
                  className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-6 mb-4 cursor-pointer hover:bg-yellow-100 transition-colors"
                  onClick={copyCode}
                >
                  <p className="text-5xl font-mono font-bold tracking-[0.3em] text-yellow-800">
                    {codeData.code}
                  </p>
                </div>
                
                <Button variant="outline" size="sm" onClick={copyCode} className="mb-4">
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Código
                </Button>
                
                {selectedAppointment && (
                  <div className="text-center mb-4">
                    <p className="font-semibold">{selectedAppointment.provider_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedAppointment.date + 'T00:00:00').toLocaleDateString('pt-BR')} às {selectedAppointment.time}
                    </p>
                    <Badge variant="outline" className="mt-2">
                      {selectedAppointment.vehicle_plate}
                    </Badge>
                  </div>
                )}
                
                <Alert className="bg-yellow-50 border-yellow-200">
                  <KeyRound className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-xs text-yellow-800">
                    Apresente este código ao retirar seu veículo.
                  </AlertDescription>
                </Alert>
              </div>
            ) : null}
            
            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button 
                variant="outline" 
                onClick={() => {
                  setCodeModalOpen(false);
                  navigate('/client/dashboard');
                }}
                data-testid="code-modal-dashboard-btn"
              >
                Voltar ao Dashboard
              </Button>
              <Button 
                onClick={() => {
                  setCodeModalOpen(false);
                  navigate('/client/schedule');
                }}
                data-testid="code-modal-schedule-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Agendamento
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Modal de Contestação */}
        <Dialog open={disputeModalOpen} onOpenChange={setDisputeModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Abrir Contestação
              </DialogTitle>
              <DialogDescription>
                Informe o motivo e descreva o problema com o serviço realizado.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="motivo">Motivo da contestação</Label>
                <Select value={disputeMotivo} onValueChange={setDisputeMotivo}>
                  <SelectTrigger id="motivo">
                    <SelectValue placeholder="Selecione o motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="servico_incompleto">Serviço incompleto</SelectItem>
                    <SelectItem value="qualidade_insatisfatoria">Qualidade insatisfatória</SelectItem>
                    <SelectItem value="danos_veiculo">Danos ao veículo</SelectItem>
                    <SelectItem value="nao_compareceu">Prestador não compareceu</SelectItem>
                    <SelectItem value="cobranca_indevida">Cobrança indevida</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="descricao">Descrição do problema</Label>
                <Textarea
                  id="descricao"
                  placeholder="Descreva detalhadamente o que aconteceu (mínimo 20 caracteres)..."
                  value={disputeDescricao}
                  onChange={(e) => setDisputeDescricao(e.target.value)}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground">
                  {disputeDescricao.length}/20 caracteres mínimos
                </p>
              </div>
              
              {disputeAppointment && (
                <div className="bg-slate-50 p-3 rounded-lg text-sm">
                  <p className="font-medium">{disputeAppointment.provider_name}</p>
                  <p className="text-muted-foreground">
                    {new Date(disputeAppointment.date + 'T00:00:00').toLocaleDateString('pt-BR')} às {disputeAppointment.time}
                  </p>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDisputeModalOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSubmitDispute} 
                disabled={disputeLoading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {disputeLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <AlertTriangle className="w-4 h-4 mr-2" />
                )}
                Enviar Contestação
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <p className="text-xs text-muted-foreground mb-4">
          Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')}
        </p>

        {/* Pass 10C-IX: nova UX em abas [Próximos] [Histórico] */}
        <Tabs value={view} onValueChange={setView} className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6" data-testid="appointments-tabs">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Próximos</TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">Histórico</TabsTrigger>
          </TabsList>

          {/* PRÓXIMOS — somente ativos (activeItems, mais próximo primeiro) */}
          <TabsContent value="upcoming" data-testid="tab-content-upcoming">
            {activeItems.length > 0 ? (
              <div className="space-y-4" data-testid="upcoming-list">
                {activeItems.map(renderAppointmentCard)}
              </div>
            ) : (
              <Card className="p-8 text-center" data-testid="upcoming-empty">
                <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">Nenhum agendamento ativo</h3>
                <p className="text-muted-foreground mb-4">Agende uma lavagem para seu veículo</p>
                <Button onClick={() => navigate('/client/schedule')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Agendar Serviço
                </Button>
              </Card>
            )}
          </TabsContent>

          {/* HISTÓRICO — lista única (historyItems, mais recente primeiro) + Ver mais */}
          <TabsContent value="history" data-testid="tab-content-history">
            {historyItems.length === 0 ? (
              <Card className="p-6 text-center" data-testid="history-empty">
                <p className="text-muted-foreground">Nenhum agendamento no histórico.</p>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="history-list">
                {historyItems.map(renderHistoryCard)}
              </div>
            )}

            {historyHasMore && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={historyLoadingMore}
                  data-testid="history-load-more-btn"
                >
                  {historyLoadingMore ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Ver mais
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
